import {
  IPatientWaitingListState,
  setAtHomeListingFilters,
  setAtHomeStatsFilters,
  setPWLFilters,
} from "@/app_redux/reducers/slices/auth/patient_waiting_list_slice";
import {
  IAtHomeSubServiceItem,
  useAtHomeSubServicesListAPI,
} from "@/app/hooks/api_hooks/AtHomeServices/useAtHomeSubServicesListAPI";
import clsx from "clsx";
import { format } from "date-fns";
import { ROUTES } from "@/app/routes";
import { useLoader } from "@/app/hooks/useLoader";
import { useForm, useWatch } from "react-hook-form";
import useRouteInfo from "@/app/hooks/useRouteInfo";
import ButtonCore from "@/app/components/ui/Buttons/ButtonCore";
import { useStringConstants } from "@/app/hooks/useStringConstants";
import { useAfterFirstRender } from "@/app/hooks/useAfterFirstRender";
import { useSelectMenuReducer } from "@/app/hooks/useSelectMenuReducer";
import ToggleWithIcon from "@/app/components/ui/Toggles/ToggleWithIcon";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import PWLCalendarComponent from "@/app/components/ui/Calendar/PWLCalendar";
import { useAppDispatch, useAppSelector } from "@/app_redux/hooks/root_hook";
import { useFeatureAccess } from "@/app/hooks/useFeatureAccess";
import { IOrgProviderListResponseData } from "@/api_framework/api_modals/Org";
import FilterRowSkeleton from "@/app/components/ui/Skeletons/FilterRowSkeleton";
import { IBookingStatusListResponseData } from "@/api_framework/api_modals/Bookings";
import SearchableMultiSelectMenu from "@/app/components/ui/SelectMenus/MultiSelectMenu";
import SearchableSelectMenu from "@/app/components/ui/SelectMenus/SearchableSelectMenu";
import { useStaffClinicListAPI } from "@/app/hooks/api_hooks/clinic/useStaffClinicListAPI";
import { useAtHomeStaffListAPI } from "@/app/hooks/api_hooks/AtHomeServices/useAtHomeStaffListAPI";
import { useAtHomeBookingStatusSummaryAPI } from "@/app/hooks/api_hooks/PWL/useAtHomeBookingStatusSummaryAPI";
import { IAtHomeListingFiltersParamsBody, ValidBookingStatusesNew } from "@/api_framework/api_modals/Patients";
import { useAtHomeServicesListingAPI } from "@/app/hooks/api_hooks/AtHomeServices/useAtHomeServicesListingAPI";
import { useAtHomeBookingStatusListAPI } from "@/app/hooks/api_hooks/AtHomeServices/useAtHomeBookingStatusListAPI";

export interface IAtHomeFilterFormFields {
  date: string;
  city: string;
  clinic: string;
  status: string;
  subservice: string[];
  provider: string;
}

interface IAtHomeServicesFiltersProps {
  currentStat: string;
  setStatCard: React.Dispatch<React.SetStateAction<string>>;
}

const ALL_APPOINTMENTS_LABEL = "All Appointments";

const AtHomeServicesFilters: React.FC<IAtHomeServicesFiltersProps> = ({ currentStat, setStatCard }) => {
  const dispatch = useAppDispatch();
  const stringConstants = useStringConstants();
  const routeId = ROUTES.ROOT.children.PATIENT_WAITING_LIST.id;
  const [subServices, setSubServices] = useState<IAtHomeSubServiceItem[]>([]);
  const [subServiceToServiceIdMap, setSubServiceToServiceIdMap] = useState<Record<string, string>>({});
  const [bookingStatuses, setBookingStatuses] = useState<IBookingStatusListResponseData[]>([]);
  const [providers, setProviders] = useState<IOrgProviderListResponseData[]>([]);
  const [initialFetched, setInitialFetched] = useState<boolean>(false);
  const [showMobileFilters, setShowMobileFilters] = useState<boolean>(false);
  const [isAtHomeOptionsLoading, setIsAtHomeOptionsLoading] = useState<boolean>(true);
  const hasLoadedFilterOptionsRef = useRef(false);
  const hasInitializedFiltersRef = useRef(false);
  const currentDate = format(new Date(Date.now()), stringConstants.fieldDOBFormat);
  const isAtHomeListLoading = useLoader("isAtHomeServicesListingInProgress");
  const isStatsLoading = useLoader("isAtHomeServicesStatsInProgress");
  const { mappedCityId, mappedClinicId } = useAppSelector((state) => state?.root.UIStates);
  const { routeState } = useRouteInfo(routeId);
  const { clinics, cities } = routeState.state as IPatientWaitingListState;
  const fetchClinicsByCity = useStaffClinicListAPI("PATIENT_WAITING_LIST");
  const fetchAtHomeListing = useAtHomeServicesListingAPI();
  const fetchAtHomeStats = useAtHomeBookingStatusSummaryAPI();
  const fetchAtHomeSubServices = useAtHomeSubServicesListAPI();
  const fetchAtHomeStatuses = useAtHomeBookingStatusListAPI();
  const fetchAtHomeStaff = useAtHomeStaffListAPI();
  // RBAC: ALL_CLINICS_MULTI_SELECT — legacy roles: super_admin, tech_admin, sales_admin
  const shouldShowAllClinicOption = useFeatureAccess("ALL_CLINICS_MULTI_SELECT");

  const formHook = useForm<IAtHomeFilterFormFields>({
    mode: "onChange",
    defaultValues: {
      date: currentDate,
      city: "",
      clinic: "",
      status: "-1",
      subservice: ["-1"],
      provider: "-1",
    },
  });
  const watchedClinic = useWatch({ control: formHook.control, name: "clinic" });
  const watchedDate = useWatch({ control: formHook.control, name: "date" });
  const watchedServices = useWatch({ control: formHook.control, name: "subservice" });
  const watchedServicesKey = useMemo(() => (watchedServices ?? []).join(","), [watchedServices]);

  const allAvailableSubServiceIds = useMemo(
    () => Array.from(new Set(subServices.map((s) => s.id).filter(Boolean))),
    [subServices],
  );

  const mapSelectedSubServiceIds = useCallback(
    (selectedSubServiceIds: string[] = []) => {
      if (!selectedSubServiceIds.length || selectedSubServiceIds[0] === "-1") {
        return allAvailableSubServiceIds;
      }
      return Array.from(new Set(selectedSubServiceIds.filter((id) => Boolean(id) && id !== "-1")));
    },
    [allAvailableSubServiceIds],
  );

  const getStatusTitles = useCallback((): string[] => {
    const titles = bookingStatuses
      .map((statusItem) => statusItem?.title)
      .filter((title): title is string => Boolean(title));
    return titles.length > 0 ? titles : ["Upcoming", "Waiting", "Attending", "Completed", "Cancelled"];
  }, [bookingStatuses]);

  const constructAtHomeFetchBody = useCallback(
    (
      formFields: IAtHomeFilterFormFields,
      providersList: IOrgProviderListResponseData[] = [],
    ): IAtHomeListingFiltersParamsBody => {
      const allStatuses = getStatusTitles();
      const bookingStatusFieldValue =
        formFields.status === ALL_APPOINTMENTS_LABEL || formFields.status === "-1" ? allStatuses : [formFields.status];
      return {
        booking_status: bookingStatusFieldValue as unknown as ValidBookingStatusesNew[],
        clinic_id: formFields.clinic === "-1" ? clinics.map((clinic) => clinic.id) : [formFields.clinic],
        date: formFields.date,
        staff_id:
          formFields.provider === "-1"
            ? (providersList ?? []).map((provider) => provider.staff_id)
            : [formFields.provider],
      };
    },
    [clinics, getStatusTitles],
  );

  const fetchAtHomeProvidersList = useCallback(
    async (formFields: IAtHomeFilterFormFields): Promise<IOrgProviderListResponseData[]> => {
      const clinicIds =
        formFields.clinic === "-1" ? clinics.map((clinic) => clinic.id) : formFields.clinic ? [formFields.clinic] : [];
      if (clinicIds.length === 0 || !formFields.date) {
        setProviders([]);
        return [];
      }
      const selectedSubServiceIds = mapSelectedSubServiceIds(formFields.subservice);
      const selectedServiceIds = Array.from(
        new Set(
          selectedSubServiceIds
            .map((subServiceId) => subServiceToServiceIdMap[subServiceId])
            .filter((serviceId): serviceId is string => Boolean(serviceId)),
        ),
      );
      const providerList = await fetchAtHomeStaff({
        bookingDate: formFields.date,
        clinicIds,
        serviceIds: selectedServiceIds,
        cityId: formFields.city === "-1" ? undefined : formFields.city,
      });
      setProviders(providerList);
      return providerList;
    },
    [clinics, fetchAtHomeStaff, mapSelectedSubServiceIds, subServiceToServiceIdMap],
  );

  const applyAtHomeFilters = useCallback(
    async (formFields: IAtHomeFilterFormFields, providersList?: IOrgProviderListResponseData[]) => {
      const body = constructAtHomeFetchBody(formFields, providersList);
      const selectedSubServiceIds = mapSelectedSubServiceIds(formFields.subservice);
      const statusIdByTitle = new Map(
        bookingStatuses
          .filter((statusItem) => Boolean(statusItem?.title) && Boolean(statusItem?.id))
          .map((statusItem) => [String(statusItem.title).toLowerCase(), String(statusItem.id)]),
      );
      const bookingStatusIds = (body.booking_status ?? [])
        .map((statusTitle) => statusIdByTitle.get(String(statusTitle ?? "").toLowerCase()))
        .filter((statusId): statusId is string => Boolean(statusId));
      const listingPayload: IAtHomeListingFiltersParamsBody = {
        ...body,
        booking_status: bookingStatusIds,
        sub_service_id: selectedSubServiceIds,
      };
      const statsPayload = {
        booking_date: body.date,
        clinic_id: body.clinic_id,
        sub_service_id: selectedSubServiceIds,
        staff_id: body.staff_id,
      };

      dispatch(
        setPWLFilters({
          filters: {
            date: body.date,
            booking_statuses: body.booking_status as unknown as ValidBookingStatusesNew[],
            clinic_ids: body.clinic_id,
            service_ids: selectedSubServiceIds,
            staff_ids: body.staff_id,
            created_by: body.created_by,
          },
        }),
      );
      dispatch(
        setAtHomeListingFilters({
          filters: listingPayload,
        }),
      );
      dispatch(
        setAtHomeStatsFilters({
          filters: statsPayload,
        }),
      );
      await fetchAtHomeListing(listingPayload);
      await fetchAtHomeStats(statsPayload);
    },
    [
      bookingStatuses,
      constructAtHomeFetchBody,
      dispatch,
      fetchAtHomeListing,
      fetchAtHomeStats,
      mapSelectedSubServiceIds,
    ],
  );

  useEffect(() => {
    if (hasLoadedFilterOptionsRef.current) return;
    hasLoadedFilterOptionsRef.current = true;
    const loadFilterOptions = async () => {
      setIsAtHomeOptionsLoading(true);
      const [subServiceListResponse, statusList] = await Promise.all([fetchAtHomeSubServices(), fetchAtHomeStatuses()]);
      setSubServices(subServiceListResponse.subServices);
      setSubServiceToServiceIdMap(subServiceListResponse.subServiceToServiceIdMap);
      setBookingStatuses(statusList);
      setIsAtHomeOptionsLoading(false);
    };
    loadFilterOptions();
  }, [fetchAtHomeStatuses, fetchAtHomeSubServices]);

  useEffect(() => {
    if (
      cities?.length === 0 ||
      clinics?.length === 0 ||
      subServices?.length === 0 ||
      bookingStatuses?.length === 0 ||
      initialFetched ||
      hasInitializedFiltersRef.current
    ) {
      return;
    }
    hasInitializedFiltersRef.current = true;
    const initialFormFields: IAtHomeFilterFormFields = {
      date: currentDate,
      city: mappedCityId ?? "-1",
      clinic: mappedClinicId ?? "-1",
      status: "-1",
      subservice: ["-1"],
      provider: "-1",
    };
    const init = async () => {
      const normalizedInitialServiceIds = mapSelectedSubServiceIds(initialFormFields.subservice);
      formHook.setValue("date", initialFormFields.date);
      formHook.setValue("city", initialFormFields.city);
      formHook.setValue("clinic", initialFormFields.clinic);
      formHook.setValue("status", initialFormFields.status);
      formHook.setValue("subservice", normalizedInitialServiceIds);
      formHook.setValue("provider", initialFormFields.provider);
      const providerList = await fetchAtHomeProvidersList(initialFormFields);
      await applyAtHomeFilters(initialFormFields, providerList);
      setInitialFetched(true);
    };
    init();
  }, [
    applyAtHomeFilters,
    bookingStatuses?.length,
    cities?.length,
    clinics?.length,
    currentDate,
    fetchAtHomeProvidersList,
    formHook,
    initialFetched,
    mappedCityId,
    mappedClinicId,
    mapSelectedSubServiceIds,
    subServices?.length,
  ]);

  useEffect(() => {
    if (!initialFetched) return;
    const formFields = formHook.getValues();
    fetchAtHomeProvidersList(formFields).then((providerList) => {
      const selectedProvider = formHook.getValues("provider");
      if (selectedProvider !== "-1" && !providerList.some((provider) => provider.staff_id === selectedProvider)) {
        formHook.setValue("provider", "-1");
      }
    });
  }, [fetchAtHomeProvidersList, formHook, initialFetched, watchedClinic, watchedDate, watchedServicesKey]);

  useAfterFirstRender(() => {
    if (!initialFetched || !currentStat) return;
    const nextStatus = currentStat?.toLowerCase() === "total" ? "-1" : currentStat;
    formHook.setValue("status", nextStatus);
    const nextFormFields = { ...formHook.getValues(), status: nextStatus };
    fetchAtHomeProvidersList(nextFormFields).then((providerList) => {
      applyAtHomeFilters(nextFormFields, providerList);
    });
  }, [currentStat]);

  const onSubmit = async (data: IAtHomeFilterFormFields) => {
    const providerList = await fetchAtHomeProvidersList(data);
    await applyAtHomeFilters(data, providerList);
    if (typeof window !== "undefined" && window.innerWidth < 1024) {
      setShowMobileFilters(false);
    }
  };

  const orgCityItems = useSelectMenuReducer(cities, "name", "id", {
    withAllSelectOption: true,
    allSelectItemOptions: {
      title: "All Cities",
    },
  });

  const orgClinicItems = useSelectMenuReducer(clinics, "display_name", "id", {
    withAllSelectOption: shouldShowAllClinicOption,
    allSelectItemOptions: {
      title: "All Clinics",
    },
  });

  const orgBookingStatusItems = useSelectMenuReducer(bookingStatuses, "title", "id", {
    withAllSelectOption: true,
    allSelectItemOptions: {
      title: ALL_APPOINTMENTS_LABEL,
    },
  });

  const orgSubServiceItems = useSelectMenuReducer(subServices, "title", "id");
  const orgProviderItems = useSelectMenuReducer(providers, "name", "staff_id", {
    withAllSelectOption: providers?.length > 0,
    allSelectItemOptions: {
      title: "All Staff",
    },
  });

  const isFiltersSelectMenuLoaded = () => {
    return (
      orgBookingStatusItems?.length > 0 &&
      orgCityItems?.length > 0 &&
      orgClinicItems?.length > 0 &&
      orgSubServiceItems?.length > 0
    );
  };

  const isSubmitLoading = isAtHomeListLoading || isStatsLoading;

  if (!isFiltersSelectMenuLoaded() || isAtHomeOptionsLoading) {
    return <FilterRowSkeleton className="mt-2" />;
  }

  return (
    <>
      <div className="lg:hidden">
        <div className="flex items-center justify-end gap-4 rounded-[10px] bg-white px-3">
          <p className="text-[0.875rem] font-semibold text-[#193A6A]">Filters</p>
          <ToggleWithIcon
            toggleState={showMobileFilters}
            onToggleChange={(status) => {
              setShowMobileFilters(status);
            }}
          />
        </div>
      </div>
      <form
        onSubmit={formHook.handleSubmit(onSubmit)}
        className={clsx(
          "flex flex-wrap lg:flex-nowrap w-full justify-between items-center gap-3 py-1 mt-2",
          !showMobileFilters && "hidden lg:flex",
        )}
      >
        <PWLCalendarComponent
          source="pwl"
          allowPastDaySelection={true}
          errorMessages={[{ message: "Date is required", type: "required" }]}
          register={formHook.register("date", {
            required: true,
          })}
          showTypedErrors={false}
          showLeftNav={true}
          fieldError={formHook.formState.errors.date}
          onSelectDate={(date) => {
            formHook.setValue("date", format(date, "yyyy-MM-dd"));
          }}
          defaultSelectedDate={formHook.watch("date")}
          className="!text-center"
          containerClassName="w-full max-w-[150px]"
        />
        <SearchableSelectMenu
          source="pwl"
          errorMessages={[{ message: "City is required", type: "required" }]}
          onSelectItem={(item) => {
            if (!item) return;
            formHook.setValue("city", item.id);
            formHook.setValue("clinic", "-1");
            formHook.setValue("provider", "-1");
            if (item.id === "-1") {
              fetchClinicsByCity({
                cityIds: cities.map((city) => city.id),
                withProviders: false,
                ROTADate: formHook.getValues("date"),
              });
              return;
            }
            fetchClinicsByCity({
              cityIds: [item.id],
              withProviders: false,
              ROTADate: formHook.getValues("date"),
            });
          }}
          fieldError={formHook.formState.errors.city}
          register={formHook.register("city", { required: true })}
          selectItems={orgCityItems}
          placeholder="Select City"
          showTooltips={false}
          showTypedErrors={false}
          showDropdownIcon={true}
          defaultSelected={orgCityItems.filter((cityItem) => cityItem.id === formHook.watch("city"))[0]}
          className="text-gray-800"
          containerClassName="w-full"
        />
        <SearchableSelectMenu
          source="pwl"
          errorMessages={[{ message: "Clinic is required", type: "required" }]}
          onSelectItem={(item) => {
            if (!item) return;
            formHook.setValue("clinic", item.id);
            formHook.setValue("provider", "-1");
          }}
          fieldError={formHook.formState.errors.clinic}
          register={formHook.register("clinic", { required: true })}
          selectItems={orgClinicItems}
          placeholder="Select Clinic"
          showTooltips={false}
          showTypedErrors={false}
          showDropdownIcon={true}
          defaultSelected={orgClinicItems.filter((clinicItem) => clinicItem.id === formHook.watch("clinic"))[0]}
          listBoxClassName="w-full sm:w-60"
          className="text-gray-800"
          containerClassName="w-full"
        />
        <SearchableSelectMenu
          source="pwl"
          errorMessages={[{ message: "Status is required", type: "required" }]}
          showSearchInputInDropdown={false}
          disableSearch={true}
          onSelectItem={(item) => {
            if (!item) return;
            formHook.setValue("status", item.title);
            setStatCard(item.title === ALL_APPOINTMENTS_LABEL ? "Total" : item.title);
          }}
          fieldError={formHook.formState.errors.status}
          register={formHook.register("status", {
            required: true,
          })}
          showTypedErrors={false}
          selectItems={orgBookingStatusItems}
          placeholder="Select Appointment Status"
          showTooltips={false}
          showDropdownIcon={true}
          defaultSelected={
            orgBookingStatusItems.filter((statusItem) => {
              if (formHook.watch("status") === "-1") {
                return statusItem.title === ALL_APPOINTMENTS_LABEL;
              }
              return statusItem.title === formHook.watch("status");
            })[0]
          }
          containerClassName="w-full"
          className="text-gray-800"
          listBoxClassName="w-full sm:w-64"
        />
        <SearchableMultiSelectMenu
          source="pwl"
          onSelectItem={(items) => {
            const selectedSubServiceIds = items.map((serviceItem) => serviceItem.id);
            formHook.setValue("subservice", selectedSubServiceIds);
            formHook.setValue("provider", "-1");
          }}
          errorMessages={[{ message: "Required", type: "required" }]}
          fieldError={formHook.formState.errors.subservice}
          register={formHook.register("subservice", {
            required: true,
          })}
          withSelectAll
          defaultSelectAll
          withDropDownSearch
          showDropdownIcon
          defaultSelected={orgSubServiceItems.filter((serviceItem) =>
            formHook.watch("subservice")?.includes(serviceItem.id),
          )}
          selectItems={orgSubServiceItems}
          placeholder="Select Sub Services"
          showTooltips={false}
          showTypedErrors={false}
          listBoxClassName="w-full sm:w-72"
          className="text-gray-800"
          containerClassName="w-full"
        />
        <SearchableSelectMenu
          source="pwl"
          errorMessages={[{ message: "Staff is required", type: "required" }]}
          onSelectItem={(item) => {
            if (item) {
              formHook.setValue("provider", item.id);
            }
          }}
          fieldError={formHook.formState.errors.provider}
          register={formHook.register("provider", { required: true })}
          selectItems={orgProviderItems}
          placeholder="Select Staff"
          showTooltips={false}
          showTypedErrors={false}
          showDropdownIcon={true}
          defaultSelected={
            orgProviderItems.filter((providerItem) => {
              if (formHook.watch("provider") === "-1") {
                return providerItem.title === "All Staff";
              }
              return providerItem.id === formHook.watch("provider");
            })[0]
          }
          listBoxClassName="w-full sm:w-64"
          className="text-gray-800"
          containerClassName="w-full"
        />
        <div className="flex items-center">
          <ButtonCore
            size="PWL"
            type="primary"
            asType="submit"
            asSubmitText="Apply Filters"
            disabled={isSubmitLoading}
            className={` cursor-pointer ${isSubmitLoading ? "cursor-not-allowed" : "cursor-pointer"}`}
          />
        </div>
      </form>
    </>
  );
};

export default AtHomeServicesFilters;
